import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Divider,
  Button,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const TermsOfUse = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Paper elevation={2} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Användarvillkor för Relco
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Senast uppdaterad: 2025-07-09
          </Typography>
          
          <Typography variant="body1" paragraph>
            Välkommen till Relco – en webbaserad tjänst ("Tjänsten") som hjälper företag att samla in feedback på ett enkelt och ansvarsfullt sätt. Genom att använda Relco (tillgänglig på app.relco.se) godkänner du följande användarvillkor ("Villkoren").
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
            Läs dessa villkor noggrant innan du börjar använda tjänsten.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            1. Tjänstens funktion
          </Typography>
          <Typography variant="body1" paragraph>
            Relco tillhandahåller en webbtjänst som hjälper företag att samla in kundfeedback och recensioner på ett effektivt sätt. Tjänsten erbjuder funktioner såsom:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body1" paragraph>
              Automatiserade sms-sekvenser för feedback och recensionsförfrågningar
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              AI-baserad analys av kundsvar
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Integration med externa system (t.ex. Fortnox, Spiris)
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Samlad hantering av konversationer
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Anpassning av meddelandemallar och kontoinställningar
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Tjänsten utvecklas löpande och kan därför komma att innehålla ytterligare funktioner utöver de ovan nämnda.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            2. Ansvar för innehåll och användning
          </Typography>
          
          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1 }}>
            2.1. Kundkommunikation
          </Typography>
          <Typography variant="body1" paragraph>
            Du som användare ansvarar för all kommunikation som skickas till dina kunder via Relco. Även om Relco möjliggör automatiserade meddelanden å dina vägnar, bär du det fulla ansvaret för innehållets utformning och hanteringen av inkommande feedback.
          </Typography>
          
          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1 }}>
            2.2. Externa policyer och riktlinjer
          </Typography>
          <Typography variant="body1" paragraph>
            Du ansvarar för att din användning av tjänsten sker i enlighet med gällande lagar samt villkor och riktlinjer från externa plattformar och tjänster du interagerar med – exempelvis recensionsplattformar såsom Google, Reco eller Trustpilot. Detta inkluderar, men är inte begränsat till, förbud mot selektiv uppmuntran till positiva recensioner ("review gating").
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
            Det är alltid ditt ansvar att säkerställa att utskick och insamling av recensioner sker i enlighet med externa policyer.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            3. Användarkonto
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body1" paragraph>
              Du loggar in med ditt mobilnummer.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Det är ditt ansvar att skydda dina inloggningsuppgifter och hålla din kontaktinformation uppdaterad.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Du får inte överlåta ditt konto till någon annan utan skriftligt tillstånd från Relco.
            </Typography>
          </Box>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            4. Databehandling och ansvar
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body1" paragraph>
              Relco behandlar personuppgifter (såsom namn, telefonnummer och feedback) för att tillhandahålla tjänsten och möjliggöra automatiserad kommunikation.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Du ansvarar för att all data du laddar upp eller använder i tjänsten är korrekt, laglig och insamlad i enlighet med gällande dataskyddslagstiftning (såsom GDPR). Du är även ansvarig för att ha nödvändigt samtycke eller annan rättslig grund för att kommunicera med dina kunder via Relco.
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Relco använder AI för att analysera kundsvar, men är inte ansvarigt för feltolkningar eller beslut som fattas baserat på AI-analysen.
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Mer information om hur vi behandlar personuppgifter finns i vår integritetspolicy:{' '}
            <Typography 
              component="a" 
              href="https://www.relco.se/integritetspolicy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              https://www.relco.se/integritetspolicy
            </Typography>
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            5. Begränsning av ansvar
          </Typography>
          <Typography variant="body1" paragraph>
            Relco ansvarar inte för:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body1" paragraph>
              Eventuella konsekvenser av användarens brott mot externa recensionstjänsters regler
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Bristande resultat i insamling av recensioner
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Felaktig tolkning av kundfeedback eller AI-analys
            </Typography>
            <Typography component="li" variant="body1" paragraph>
              Förlorad data eller tekniska fel i tredjepartssystem (t.ex. Fortnox)
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Tjänsten tillhandahålls i befintligt skick ("as is") och utan några garantier om tillgänglighet, exakthet eller lämplighet för ett visst syfte. Relco garanterar inte att tjänsten är fri från fel, avbrott eller störningar och ansvarar inte för skador eller förluster till följd av detta.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            6. Uppsägning och avslut
          </Typography>
          <Typography variant="body1" paragraph>
            Du kan när som helst avsluta ditt konto genom att kontakta oss. Relco förbehåller sig rätten att avsluta eller begränsa åtkomst till tjänsten om du bryter mot dessa villkor eller använder tjänsten i strid med tillämplig lag.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            7. Ändringar i villkoren
          </Typography>
          <Typography variant="body1" paragraph>
            Relco kan komma att uppdatera dessa villkor. Om större ändringar sker meddelas du via e-post eller i appen. Den senaste versionen finns alltid tillgänglig på denna sida.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            8. Priser och betalning
          </Typography>
          <Typography variant="body1" paragraph>
            Prissättning och betalningsvillkor regleras i separata avtal mellan Relco och varje kund. Eventuella priser som visas på vår webbplats är vägledande och utgör inte ett bindande erbjudande.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            9. Kontakt
          </Typography>
          <Typography variant="body1" paragraph>
            Har du frågor? Kontakta oss på:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body1" paragraph>
              📧 info@relco.se
            </Typography>
            <Typography variant="body1" paragraph>
              🌐{' '}
              <Typography 
                component="a" 
                href="https://www.relco.se/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                https://www.relco.se/
              </Typography>
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleGoBack}
              startIcon={<ArrowBackIcon />}
            >
              Tillbaka
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfUse; 